/**
 * @fileoverview Pre-Appointment Labels manager (MOR-882)
 *
 * Firm-defined tags applied to pre-appointment jobs for filtering and
 * grouping. Admin/owner only — `canEdit` gates writes; mutations enforce the
 * same rule. System labels can be renamed, recoloured, and reordered but not
 * deleted.
 *
 * Visual language mirrors the Task Labels manager for consistency.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@cvx/_generated/api";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { cn } from "@/utils/misc";
import { ColorSwatchPicker } from "../tasks/ColorSwatchPicker";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

type PreAppointmentLabel = FunctionReturnType<
  typeof api.preAppointment.labels.listByOrg
>[number];

export function PreAppointmentLabelsManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: labels } = useQuery(
    convexQuery(api.preAppointment.labels.listByOrg, { orgId }),
  );
  const reorder = useConvexMutation(api.preAppointment.labels.reorder);
  const [showNewForm, setShowNewForm] = useState(false);
  const rows = useMemo(() => labels ?? [], [labels]);

  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => a.sortOrder - b.sortOrder),
    [rows],
  );

  // Drag-to-reorder (mirrors the statuses manager). A 6px activation distance
  // keeps row clicks/buttons usable; only the grip handle starts a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = orderedRows.map((l) => l._id);
    const from = ids.indexOf(active.id as Id<"preAppointmentLabels">);
    const to = ids.indexOf(over.id as Id<"preAppointmentLabels">);
    if (from < 0 || to < 0) return;
    reorder({ orgId, orderedIds: arrayMove(ids, from, to) }).catch(
      (err: Error) => toast.error(err.message || "Could not reorder labels."),
    );
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need owner or admin access to manage pipeline labels.
            </p>
          </div>
        </div>
      )}

      <div className={GROUP_CARD_CLASS}>
        <div className="flex items-start justify-between gap-6 border-b border-border/50 p-6">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-primary">
              Pipeline Labels
            </h3>
            <p className="mt-1 text-sm text-primary/60">
              Add and manage labels that can be applied to your pipeline tracker
              to aid filtering and grouping. Drag labels to reorder them.
            </p>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {rows.length} {rows.length === 1 ? "label" : "labels"}
          </Badge>
        </div>

        <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
          <h4 className="text-sm font-semibold text-primary">Labels</h4>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewForm(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              Add label
            </Button>
          )}
        </div>

        <div className="divide-y divide-border/30">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedRows.map((l) => l._id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedRows.map((label, index) => (
                <LabelRow
                  key={label._id}
                  orgId={orgId}
                  label={label}
                  canEdit={canEdit}
                  isFirst={index === 0}
                  isLast={index === orderedRows.length - 1}
                  orderedIds={orderedRows.map((l) => l._id)}
                  index={index}
                />
              ))}
            </SortableContext>
          </DndContext>

          {rows.length === 0 && !showNewForm && (
            <div className="px-6 py-8 text-center">
              <p className="text-sm text-primary/40">No labels yet.</p>
            </div>
          )}

          {showNewForm && (
            <NewLabelForm orgId={orgId} onClose={() => setShowNewForm(false)} />
          )}
        </div>
      </div>
    </div>
  );
}

function LabelRow({
  orgId,
  label,
  canEdit,
  isFirst,
  isLast,
  orderedIds,
  index,
}: {
  orgId: Id<"organizations">;
  label: PreAppointmentLabel;
  canEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  orderedIds: Array<Id<"preAppointmentLabels">>;
  index: number;
}) {
  const [editing, setEditing] = useState(false);
  const update = useConvexMutation(api.preAppointment.labels.update);
  const reorder = useConvexMutation(api.preAppointment.labels.reorder);
  const archive = useConvexMutation(api.preAppointment.labels.archive);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: label._id, disabled: !canEdit || editing });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const move = (direction: -1 | 1) => {
    const next = [...orderedIds];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder({ orgId, orderedIds: next }).catch((e: Error) =>
      toast.error(e.message || "Could not reorder labels."),
    );
  };

  if (editing) {
    return (
      <div ref={setNodeRef} style={style} className="px-6 py-4">
        <LabelForm
          initial={{ name: label.name, color: label.color }}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (state) => {
            await update({
              labelId: label._id,
              patch: { name: state.name.trim(), color: state.color },
            });
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 bg-background px-6 py-3",
        isDragging && "relative z-10 shadow-lg",
      )}
    >
      {canEdit && (
        <span
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-primary/25 hover:text-primary/60 active:cursor-grabbing"
          title="Drag to reorder"
          aria-label={`Drag ${label.name} to reorder`}
        >
          <GripVertical className="size-4" />
        </span>
      )}
      {canEdit && (
        <div className="flex flex-col">
          <button
            type="button"
            disabled={isFirst}
            onClick={() => move(-1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move up"
          >
            <ArrowUp className="size-3" />
          </button>
          <button
            type="button"
            disabled={isLast}
            onClick={() => move(1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move down"
          >
            <ArrowDown className="size-3" />
          </button>
        </div>
      )}
      <Badge color={label.color} variant="soft" className="shrink-0">
        {label.name}
      </Badge>
      <div className="min-w-0 flex-1">
        {label.isSystem && (
          <span className="text-xs text-primary/40">System</span>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-3.5" />
          </Button>
          {label.isSystem ? (
            <span title="System labels can't be removed">
              <Lock className="size-3.5 text-primary/30" />
            </span>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              className="size-7 text-destructive hover:text-destructive"
              onClick={() => {
                archive({ labelId: label._id }).catch((e: Error) =>
                  toast.error(e.message || "Could not remove label."),
                );
              }}
            >
              <Trash2 className="size-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function NewLabelForm({
  orgId,
  onClose,
}: {
  orgId: Id<"organizations">;
  onClose: () => void;
}) {
  const create = useConvexMutation(api.preAppointment.labels.create);
  return (
    <div className="bg-muted/20 px-6 py-4">
      <LabelForm
        initial={{ name: "", color: "blue" }}
        submitLabel="Add label"
        onCancel={onClose}
        onSubmit={async (state) => {
          await create({ orgId, name: state.name.trim(), color: state.color });
          onClose();
        }}
      />
    </div>
  );
}

interface LabelFormState {
  name: string;
  color: TaskColor;
}

function LabelForm({
  initial,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: LabelFormState;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (state: LabelFormState) => Promise<void>;
}) {
  const [state, setState] = useState<LabelFormState>(initial);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!state.name.trim()) {
      toast.error("Label name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(state);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not save label."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Name
        </Label>
        <Input
          value={state.name}
          autoFocus
          onChange={(e) => setState({ ...state, name: e.target.value })}
          placeholder="Referral, Urgent…"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Colour
        </Label>
        <ColorSwatchPicker
          value={state.color}
          onChange={(color) => setState({ ...state, color })}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="gap-1">
          <X className="size-3.5" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting}
          className="gap-1.5"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
